import { 
  collection, 
  query, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  Timestamp,
  orderBy
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface ProductData {
  id?: string;
  name: string;
  bank: string;
  category: string;
  minCibil: number;
  minSalary: number;
  roiRange: string;
  processingFee: string;
  maxTenure: string;
  productDetails?: string;
  salesManagerNotes?: string;
  customerNotes?: string;
  createdBy?: string;
  createdAt?: Date | Timestamp;
  updatedAt?: Date | Timestamp;
}

/**
 * Subscribe to products collection with real-time updates
 */
export function subscribeToProducts(
  callback: (products: ProductData[]) => void,
  onError?: (error: Error) => void
): () => void {
  const productsRef = collection(db, 'products');
  const productsQuery = query(productsRef, orderBy('createdAt', 'desc'));

  const unsubscribe = onSnapshot(
    productsQuery,
    (snapshot) => {
      const products: ProductData[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name || '',
          bank: data.bank || '',
          category: data.category || '',
          minCibil: data.minCibil || 0,
          minSalary: data.minSalary || 0,
          roiRange: data.roiRange || '',
          processingFee: data.processingFee || '',
          maxTenure: data.maxTenure || '',
          productDetails: data.productDetails || '',
          salesManagerNotes: data.salesManagerNotes || '',
          customerNotes: data.customerNotes || '',
          createdBy: data.createdBy,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
        };
      });
      callback(products);
    },
    (error) => {
      console.error('Error fetching products:', error);
      if (onError) {
        onError(error);
      }
    }
  );

  return unsubscribe;
}

/**
 * Add a new product
 */
export async function addProduct(product: Omit<ProductData, 'id'>): Promise<string> {
  try {
    const productsRef = collection(db, 'products');
    const docRef = await addDoc(productsRef, {
      ...product,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding product:', error);
    throw error;
  }
}

/**
 * Update an existing product
 */
export async function updateProduct(
  productId: string, 
  updates: Partial<ProductData>
): Promise<void> {
  try {
    const productRef = doc(db, 'products', productId);
    await updateDoc(productRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
}

/**
 * Delete a product
 */
export async function deleteProduct(productId: string): Promise<void> {
  try {
    const productRef = doc(db, 'products', productId);
    await deleteDoc(productRef);
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
}
